/**
 * Super-admin HR — HoodDesk company team.
 *
 * Single-page surface with 5 tabs:
 *   1. Employees    — table + add/edit/terminate
 *   2. Attendance   — month grid + bulk mark-present
 *   3. Leaves       — pending queue + history + apply
 *   4. Payroll      — month picker + run-payroll + mark-paid
 *   5. Hiring       — openings + stage-grouped applicants
 *
 * All API calls hit `/v1/superadmin/hr/*`. Cross-tenant by definition.
 */
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Briefcase,
  Plus,
  Users2,
  CalendarCheck2,
  Receipt,
  UserPlus,
  Download,
  MoreVertical,
  Check,
  X as XIcon,
  AlertCircle,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { api, get, post, patch, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatDate, formatDateTime, todayIsoDate } from '@/lib/format';

// ──────────────────────────────────────────────────────────────────────
// Constants + types — mirror server enums
// ──────────────────────────────────────────────────────────────────────
const DEPARTMENTS = [
  'founders_office',
  'product',
  'engineering',
  'design',
  'sales',
  'marketing',
  'customer_success',
  'accounting',
  'legal_compliance',
  'operations',
  'human_resources',
  'it_infrastructure',
  'data_analytics'
] as const;
type Department = (typeof DEPARTMENTS)[number];

const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'intern', 'consultant'] as const;
type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

const EMPLOYEE_STATUSES = ['active', 'on_leave', 'notice_period', 'inactive', 'terminated'] as const;
type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

const ATTENDANCE_STATUSES = [
  'present',
  'wfh',
  'half_day',
  'paid_leave',
  'unpaid_leave',
  'sick_leave',
  'holiday',
  'absent'
] as const;
type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

const LEAVE_TYPES = ['paid', 'sick', 'unpaid', 'comp_off', 'bereavement', 'maternity', 'paternity'] as const;
type LeaveType = (typeof LEAVE_TYPES)[number];
const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
type LeaveStatus = (typeof LEAVE_STATUSES)[number];

const PAYROLL_STATUSES = ['draft', 'pending', 'paid', 'cancelled'] as const;
type PayrollStatus = (typeof PAYROLL_STATUSES)[number];

const HIRING_STAGES = [
  'open',
  'screening',
  'interview_round_1',
  'interview_round_2',
  'final_round',
  'offer_extended',
  'hired',
  'rejected',
  'withdrawn'
] as const;
type HiringStage = (typeof HIRING_STAGES)[number];

const DEPT_LABEL: Record<Department, string> = {
  founders_office: "Founder's Office",
  product: 'Product',
  engineering: 'Engineering',
  design: 'Design',
  sales: 'Sales',
  marketing: 'Marketing',
  customer_success: 'Customer Success',
  accounting: 'Accounting',
  legal_compliance: 'Legal & Compliance',
  operations: 'Operations',
  human_resources: 'Human Resources',
  it_infrastructure: 'IT & Infrastructure',
  data_analytics: 'Data & Analytics'
};

const EMPLOYMENT_LABEL: Record<EmploymentType, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  intern: 'Intern',
  consultant: 'Consultant'
};

const ATTENDANCE_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  wfh: 'WFH',
  half_day: 'Half-day',
  paid_leave: 'Paid leave',
  unpaid_leave: 'Unpaid leave',
  sick_leave: 'Sick leave',
  holiday: 'Holiday',
  absent: 'Absent'
};

const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  paid: 'Paid',
  sick: 'Sick',
  unpaid: 'Unpaid',
  comp_off: 'Comp-off',
  bereavement: 'Bereavement',
  maternity: 'Maternity',
  paternity: 'Paternity'
};

const STAGE_LABEL: Record<HiringStage, string> = {
  open: 'New',
  screening: 'Screening',
  interview_round_1: 'Interview R1',
  interview_round_2: 'Interview R2',
  final_round: 'Final round',
  offer_extended: 'Offer extended',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn'
};

interface Employee {
  id: string;
  full_name: string;
  phone: string;
  email: string;
  department: Department;
  designation: string;
  employment_type: EmploymentType;
  status: EmployeeStatus;
  monthly_ctc_inr: number;
  joining_date: string;
  exit_date: string | null;
  paid_leave_balance: number;
  sick_leave_balance: number;
  created_at: string;
}

interface AttendanceRow {
  id: string;
  employee_id: string;
  date_local: string;
  status: AttendanceStatus;
  hours_worked: number | null;
  check_in_at: string | null;
  check_out_at: string | null;
}

interface LeaveRow {
  id: string;
  employee_id: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  status: LeaveStatus;
  rejection_reason: string | null;
  created_at: string;
  employee: { id: string; full_name: string; department: Department };
}

interface PayrollRow {
  id: string;
  employee_id: string;
  period_label: string;
  base_ctc_inr: number;
  attendance_days_worked: number;
  attendance_total_days: number;
  unpaid_leave_days: number;
  deductions_inr: number;
  bonus_inr: number;
  reimbursements_inr: number;
  tds_inr: number;
  net_pay_inr: number;
  status: PayrollStatus;
  paid_at: string | null;
  paid_via: string | null;
  payment_reference: string | null;
  employee: { id: string; full_name: string; department: Department; designation: string };
}

interface JobOpening {
  id: string;
  title: string;
  department: Department;
  employment_type: EmploymentType;
  location: string | null;
  description: string | null;
  min_ctc_inr: number | null;
  max_ctc_inr: number | null;
  is_open: boolean;
  opened_at: string;
  closed_at: string | null;
  _count?: { applicants: number };
}

interface Applicant {
  id: string;
  job_opening_id: string;
  full_name: string;
  phone: string;
  email: string;
  expected_ctc_inr: number | null;
  experience_years: number | null;
  stage: HiringStage;
  rating: number | null;
  applied_at: string;
  opening?: { id: string; title: string; department: Department };
}

type TabId = 'employees' | 'attendance' | 'leaves' | 'payroll' | 'hiring';

// ──────────────────────────────────────────────────────────────────────
// Formatting helpers
// ──────────────────────────────────────────────────────────────────────

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `Rs ${n.toLocaleString('en-IN')}`;
}

function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const ATTENDANCE_TONE: Record<AttendanceStatus, string> = {
  present: 'bg-vt-safe-100 text-vt-safe',
  wfh: 'bg-vt-info-100 text-vt-info',
  half_day: 'bg-vt-warn-100 text-vt-warn',
  paid_leave: 'bg-vt-primary-100 text-vt-primary',
  unpaid_leave: 'bg-vt-alert-100 text-vt-alert',
  sick_leave: 'bg-vt-warn-100 text-vt-warn',
  holiday: 'bg-vt-secondary-100 text-vt-text-muted',
  absent: 'bg-vt-alert-100 text-vt-alert'
};

// ──────────────────────────────────────────────────────────────────────
// Top-level page
// ──────────────────────────────────────────────────────────────────────
export function SuperAdminHrPage(): JSX.Element {
  const [tab, setTab] = useState<TabId>('employees');

  // Page-wide KPI strip: counts pulled live from queries the tabs already make.
  const employeesQ = useQuery<{ items: Employee[] }>({
    queryKey: ['hr', 'employees'],
    queryFn: () => get('/superadmin/hr/employees?limit=100')
  });
  const leavesPendingQ = useQuery<{ items: LeaveRow[] }>({
    queryKey: ['hr', 'leaves', 'pending'],
    queryFn: () => get('/superadmin/hr/leaves?status=pending&limit=100')
  });
  const openingsQ = useQuery<{ items: JobOpening[] }>({
    queryKey: ['hr', 'job-openings', { is_open: true }],
    queryFn: () => get('/superadmin/hr/job-openings?is_open=true&limit=100')
  });

  const today = useMemo(() => todayIsoDate(), []);
  const todaysAttendanceQ = useQuery<{ items: AttendanceRow[] }[]>({
    queryKey: ['hr', 'attendance', 'today-onleave', today, employeesQ.data?.items?.map((e) => e.id) ?? []],
    enabled: (employeesQ.data?.items?.length ?? 0) > 0,
    queryFn: async () => {
      const employees = employeesQ.data?.items ?? [];
      const results = await Promise.all(
        employees.map((e) =>
          get<{ items: AttendanceRow[] }>(
            `/superadmin/hr/employees/${e.id}/attendance?from=${today}&to=${today}`
          )
        )
      );
      return results;
    }
  });

  const employees = employeesQ.data?.items ?? [];
  const activeCount = employees.filter((e) => e.status === 'active').length;
  const pendingLeaves = leavesPendingQ.data?.items?.length ?? 0;
  const openPositions = openingsQ.data?.items?.length ?? 0;
  const onLeaveToday = useMemo(() => {
    const flat = (todaysAttendanceQ.data ?? []).flatMap((r) => r.items);
    return flat.filter((a) =>
      a.status === 'paid_leave' || a.status === 'sick_leave' || a.status === 'unpaid_leave'
    ).length;
  }, [todaysAttendanceQ.data]);

  return (
    <>
      <PageHeader
        title="Human resources"
        description="HoodDesk company team — employees, attendance, leaves, payroll, and hiring."
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
          <KpiCard
            label="Active employees"
            value={employeesQ.isLoading ? '—' : String(activeCount)}
            icon={<Users2 className="h-5 w-5" strokeWidth={1.75} />}
            tone="primary"
          />
          <KpiCard
            label="On leave today"
            value={todaysAttendanceQ.isLoading ? '—' : String(onLeaveToday)}
            icon={<CalendarCheck2 className="h-5 w-5" strokeWidth={1.75} />}
            tone="warn"
          />
          <KpiCard
            label="Open positions"
            value={openingsQ.isLoading ? '—' : String(openPositions)}
            icon={<Briefcase className="h-5 w-5" strokeWidth={1.75} />}
            tone="info"
          />
          <KpiCard
            label="Pending leaves"
            value={leavesPendingQ.isLoading ? '—' : String(pendingLeaves)}
            icon={<AlertCircle className="h-5 w-5" strokeWidth={1.75} />}
            tone="alert"
          />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-vt-2 border-b border-vt-border">
          <TabButton active={tab === 'employees'} onClick={() => setTab('employees')}>
            Employees
          </TabButton>
          <TabButton active={tab === 'attendance'} onClick={() => setTab('attendance')}>
            Attendance
          </TabButton>
          <TabButton active={tab === 'leaves'} onClick={() => setTab('leaves')}>
            Leaves
          </TabButton>
          <TabButton active={tab === 'payroll'} onClick={() => setTab('payroll')}>
            Payroll
          </TabButton>
          <TabButton active={tab === 'hiring'} onClick={() => setTab('hiring')}>
            Hiring
          </TabButton>
        </div>

        {tab === 'employees' && <EmployeesTab employees={employees} loading={employeesQ.isLoading} />}
        {tab === 'attendance' && <AttendanceTab employees={employees} />}
        {tab === 'leaves' && <LeavesTab employees={employees} />}
        {tab === 'payroll' && <PayrollTab employees={employees} />}
        {tab === 'hiring' && <HiringTab employees={employees} />}
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Shared atoms
// ──────────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  icon,
  tone
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: 'primary' | 'warn' | 'info' | 'alert';
}): JSX.Element {
  const toneClass =
    tone === 'primary'
      ? 'bg-vt-primary-100 text-vt-primary'
      : tone === 'warn'
        ? 'bg-vt-warn-100 text-vt-warn'
        : tone === 'info'
          ? 'bg-vt-info-100 text-vt-info'
          : 'bg-vt-alert-100 text-vt-alert';
  return (
    <Card>
      <CardContent className="flex items-center gap-vt-3 p-vt-4">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md ${toneClass}`}>
          {icon}
        </span>
        <div>
          <div className="text-vt-12 text-vt-text-muted">{label}</div>
          <div className="text-vt-24 font-semibold text-vt-text">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'relative px-vt-4 py-vt-2 text-vt-14 transition-colors ' +
        (active ? 'text-vt-primary font-semibold' : 'text-vt-text-muted hover:text-vt-text')
      }
    >
      {children}
      {active ? <span className="absolute inset-x-0 bottom-[-1px] h-0.5 rounded-full bg-vt-primary" /> : null}
    </button>
  );
}

function StatusBadge({ status }: { status: EmployeeStatus }): JSX.Element {
  const variant =
    status === 'active' ? 'ok' : status === 'terminated' ? 'alert' : status === 'on_leave' ? 'warn' : 'muted';
  const label = status.replace(/_/g, ' ');
  return <Badge variant={variant}>{label}</Badge>;
}

// ──────────────────────────────────────────────────────────────────────
// Employees tab
// ──────────────────────────────────────────────────────────────────────
const EmployeeFormSchema = z.object({
  full_name: z.string().trim().min(2, 'Required').max(120),
  phone: z.string().trim().regex(/^\+?[1-9]\d{6,14}$/, 'Invalid phone'),
  email: z.string().trim().email('Invalid email').max(160),
  department: z.enum(DEPARTMENTS),
  designation: z.string().trim().min(2, 'Required').max(120),
  employment_type: z.enum(EMPLOYMENT_TYPES),
  monthly_ctc_inr: z.coerce.number().int().min(0).max(100_000_000),
  joining_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Required')
});
type EmployeeForm = z.infer<typeof EmployeeFormSchema>;

function EmployeesTab({
  employees,
  loading
}: {
  employees: Employee[];
  loading: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState<Employee | null>(null);

  const form = useForm<EmployeeForm>({
    resolver: zodResolver(EmployeeFormSchema),
    defaultValues: {
      full_name: '',
      phone: '',
      email: '',
      department: 'engineering',
      designation: '',
      employment_type: 'full_time',
      monthly_ctc_inr: 0,
      joining_date: todayIsoDate()
    }
  });

  const createMutation = useMutation({
    mutationFn: (v: EmployeeForm) => post<Employee>('/superadmin/hr/employees', v),
    onSuccess: () => {
      toast.success('Employee added');
      void qc.invalidateQueries({ queryKey: ['hr'] });
      setAddOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const terminateMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/superadmin/hr/employees/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Employee terminated');
      void qc.invalidateQueries({ queryKey: ['hr'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="mb-vt-3 flex items-center justify-between">
          <div className="text-vt-14 text-vt-text-muted">{employees.length} employees</div>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add employee
          </Button>
        </div>

        {loading ? (
          <div className="flex flex-col gap-vt-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : employees.length === 0 ? (
          <EmptyState
            icon={<Users2 className="h-6 w-6" strokeWidth={1.75} />}
            title="No employees yet"
            description="Add your first HoodDesk team member."
            action={
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> Add employee
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Monthly CTC</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>
                    <div className="flex items-center gap-vt-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
                        {e.full_name
                          .split(' ')
                          .map((w) => w[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-vt-text">{e.full_name}</div>
                        <div className="truncate text-vt-12 text-vt-text-soft">{e.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">{DEPT_LABEL[e.department]}</Badge>
                  </TableCell>
                  <TableCell className="text-vt-text-muted">{e.designation}</TableCell>
                  <TableCell className="text-vt-text-muted">{EMPLOYMENT_LABEL[e.employment_type]}</TableCell>
                  <TableCell>
                    <StatusBadge status={e.status} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-vt-text-muted">
                    {formatDate(e.joining_date)}
                  </TableCell>
                  <TableCell className="text-right text-vt-text-muted">{formatInr(e.monthly_ctc_inr)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end">
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button
                            type="button"
                            aria-label="Employee actions"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content
                            align="end"
                            sideOffset={6}
                            className="z-50 min-w-44 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface p-1 shadow-vt-3"
                          >
                            <DropdownMenu.Item
                              disabled={e.status === 'terminated'}
                              className="flex cursor-pointer items-center gap-2 rounded-vt-sm px-vt-3 py-vt-2 text-vt-13 text-vt-alert outline-none data-[highlighted]:bg-vt-alert/10 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
                              onSelect={() => setTerminateTarget(e)}
                            >
                              <XIcon className="h-3.5 w-3.5" /> Terminate
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add employee dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(o) => {
          setAddOpen(o);
          if (!o) form.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add HoodDesk team member</DialogTitle>
            <DialogDescription>Creates an HR employee record. Phone and email must be unique.</DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-vt-3"
            onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
          >
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emp-name">Full name</Label>
                <Input id="emp-name" autoFocus {...form.register('full_name')} />
                {form.formState.errors.full_name && (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.full_name.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emp-phone">Phone</Label>
                <Input id="emp-phone" placeholder="+919999999999" {...form.register('phone')} />
                {form.formState.errors.phone && (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.phone.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="emp-email">Email</Label>
                <Input id="emp-email" type="email" {...form.register('email')} />
                {form.formState.errors.email && (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.email.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emp-dept">Department</Label>
                <Select id="emp-dept" {...form.register('department')}>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {DEPT_LABEL[d]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emp-desig">Designation</Label>
                <Input id="emp-desig" {...form.register('designation')} />
                {form.formState.errors.designation && (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.designation.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emp-emp-type">Employment type</Label>
                <Select id="emp-emp-type" {...form.register('employment_type')}>
                  {EMPLOYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {EMPLOYMENT_LABEL[t]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="emp-ctc">Monthly CTC (INR)</Label>
                <Input id="emp-ctc" type="number" min="0" {...form.register('monthly_ctc_inr')} />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="emp-join">Joining date</Label>
                <Input id="emp-join" type="date" {...form.register('joining_date')} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={createMutation.isPending} loadingText="Adding…">
                Add employee
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(terminateTarget)}
        onOpenChange={(v) => (v ? null : setTerminateTarget(null))}
        title="Terminate employee"
        description={
          terminateTarget ? (
            <>
              Terminate <strong>{terminateTarget.full_name}</strong>? They are removed from the
              active roster and lose admin access immediately. Historical payroll/attendance is
              retained.
            </>
          ) : null
        }
        confirmLabel="Terminate"
        variant="danger"
        loading={terminateMutation.isPending}
        onConfirm={() => {
          if (!terminateTarget) return;
          terminateMutation.mutate(terminateTarget.id, {
            onSuccess: () => setTerminateTarget(null)
          });
        }}
      />
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Attendance tab
// ──────────────────────────────────────────────────────────────────────

function AttendanceTab({ employees }: { employees: Employee[] }): JSX.Element {
  const qc = useQueryClient();
  const [month, setMonth] = useState(thisMonth());
  const today = todayIsoDate();

  const bulkMutation = useMutation({
    mutationFn: () =>
      post<{ inserted: number; total_active: number }>('/superadmin/hr/attendance/bulk-mark-present', {
        date_local: today
      }),
    onSuccess: (res) => {
      toast.success(`Marked ${res.inserted} of ${res.total_active} present`);
      void qc.invalidateQueries({ queryKey: ['hr', 'attendance'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const downloadCsv = (): void => {
    void api
      .get('/superadmin/hr/attendance/export.csv', {
        responseType: 'blob',
        params: { from: `${month}-01`, to: `${month}-31` }
      })
      .then((res) => {
        const blob = new Blob([res.data as Blob], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hr-attendance-${month}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="mb-vt-4 flex flex-wrap items-center justify-between gap-vt-3">
          <div className="flex items-center gap-vt-2">
            <Label htmlFor="att-month" className="text-vt-13 text-vt-text-muted">
              Month:
            </Label>
            <Input
              id="att-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex items-center gap-vt-2">
            <Button variant="secondary" size="sm" onClick={() => bulkMutation.mutate()} loading={bulkMutation.isPending}>
              <Check className="h-4 w-4" /> Mark today: all present
            </Button>
            <Button variant="secondary" size="sm" onClick={downloadCsv}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>

        {employees.length === 0 ? (
          <EmptyState
            icon={<CalendarCheck2 className="h-6 w-6" strokeWidth={1.75} />}
            title="Add employees first"
            description="Attendance grid populates once the team is on board."
          />
        ) : (
          <AttendanceGrid employees={employees} month={month} />
        )}
      </CardContent>
    </Card>
  );
}

function AttendanceGrid({ employees, month }: { employees: Employee[]; month: string }): JSX.Element {
  const qc = useQueryClient();
  const totalDays = useMemo(() => {
    const [y, m] = month.split('-').map((s) => Number(s));
    return new Date(y!, m!, 0).getDate();
  }, [month]);
  const fromIso = `${month}-01`;
  const toIso = `${month}-${String(totalDays).padStart(2, '0')}`;

  // One query per employee — keeps it simple; team is small.
  const attendanceQ = useQuery<Record<string, Record<string, AttendanceStatus>>>({
    queryKey: ['hr', 'attendance', 'grid', month, employees.map((e) => e.id).join('|')],
    enabled: employees.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        employees.map((e) =>
          get<{ items: AttendanceRow[] }>(
            `/superadmin/hr/employees/${e.id}/attendance?from=${fromIso}&to=${toIso}`
          ).then((r) => ({ id: e.id, items: r.items }))
        )
      );
      const map: Record<string, Record<string, AttendanceStatus>> = {};
      for (const r of results) {
        map[r.id] = {};
        for (const a of r.items) map[r.id]![a.date_local] = a.status;
      }
      return map;
    }
  });

  const markMutation = useMutation({
    mutationFn: (vars: { employeeId: string; date: string; status: AttendanceStatus }) =>
      post(`/superadmin/hr/employees/${vars.employeeId}/attendance`, {
        date_local: vars.date,
        status: vars.status
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hr', 'attendance'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  const cycleStatus = (current: AttendanceStatus | undefined): AttendanceStatus => {
    const order: AttendanceStatus[] = ['present', 'wfh', 'half_day', 'paid_leave', 'unpaid_leave', 'absent'];
    if (!current) return 'present';
    const idx = order.indexOf(current);
    return order[(idx + 1) % order.length] ?? 'present';
  };

  return (
    <div className="overflow-x-auto">
      {attendanceQ.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <table className="min-w-full border-collapse text-vt-12">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 border-b border-r border-vt-border bg-vt-surface px-vt-2 py-vt-2 text-left text-vt-text-muted">
                Employee
              </th>
              {days.map((d) => (
                <th
                  key={d}
                  className="border-b border-vt-border px-1 py-vt-2 text-center text-vt-text-muted"
                  style={{ width: 28 }}
                >
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id}>
                <td className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-vt-border bg-vt-surface px-vt-2 py-vt-2 text-vt-text">
                  {e.full_name}
                </td>
                {days.map((d) => {
                  const dateStr = `${month}-${String(d).padStart(2, '0')}`;
                  const current = attendanceQ.data?.[e.id]?.[dateStr];
                  return (
                    <td key={d} className="border-b border-vt-border p-0.5 text-center">
                      <button
                        type="button"
                        title={current ? ATTENDANCE_LABEL[current] : 'Not marked'}
                        onClick={() =>
                          markMutation.mutate({
                            employeeId: e.id,
                            date: dateStr,
                            status: cycleStatus(current)
                          })
                        }
                        className={
                          'inline-flex h-6 w-6 items-center justify-center rounded-vt-sm text-[10px] font-semibold transition ' +
                          (current ? ATTENDANCE_TONE[current] : 'bg-vt-surface-2 text-vt-text-soft hover:bg-vt-secondary-100')
                        }
                      >
                        {current ? attendanceShortcut(current) : '·'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-vt-3 flex flex-wrap gap-vt-2 text-vt-12 text-vt-text-soft">
        Click cell to cycle: Present → WFH → Half-day → Paid leave → Unpaid leave → Absent.
      </div>
    </div>
  );
}

function attendanceShortcut(s: AttendanceStatus): string {
  switch (s) {
    case 'present':
      return 'P';
    case 'wfh':
      return 'W';
    case 'half_day':
      return 'H';
    case 'paid_leave':
      return 'L';
    case 'unpaid_leave':
      return 'U';
    case 'sick_leave':
      return 'S';
    case 'holiday':
      return 'O';
    case 'absent':
      return 'A';
  }
}

// ──────────────────────────────────────────────────────────────────────
// Leaves tab
// ──────────────────────────────────────────────────────────────────────
const LeaveFormSchema = z.object({
  employee_id: z.string().uuid('Pick an employee'),
  leave_type: z.enum(LEAVE_TYPES),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Required'),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Required'),
  reason: z.string().trim().max(500).optional()
});
type LeaveForm = z.infer<typeof LeaveFormSchema>;

function LeavesTab({ employees }: { employees: Employee[] }): JSX.Element {
  const qc = useQueryClient();
  const [applyOpen, setApplyOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const pendingQ = useQuery<{ items: LeaveRow[] }>({
    queryKey: ['hr', 'leaves', 'pending'],
    queryFn: () => get('/superadmin/hr/leaves?status=pending&limit=100')
  });
  const historyQ = useQuery<{ items: LeaveRow[] }>({
    queryKey: ['hr', 'leaves', 'history'],
    queryFn: () => get('/superadmin/hr/leaves?limit=50')
  });

  const form = useForm<LeaveForm>({
    resolver: zodResolver(LeaveFormSchema),
    defaultValues: {
      employee_id: employees[0]?.id ?? '',
      leave_type: 'paid',
      start_date: todayIsoDate(),
      end_date: todayIsoDate(),
      reason: ''
    }
  });

  const applyMutation = useMutation({
    mutationFn: (v: LeaveForm) => post<LeaveRow>('/superadmin/hr/leaves', v),
    onSuccess: () => {
      toast.success('Leave applied');
      void qc.invalidateQueries({ queryKey: ['hr', 'leaves'] });
      setApplyOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const approveMutation = useMutation({
    mutationFn: (id: string) => post(`/superadmin/hr/leaves/${id}/approve`, {}),
    onSuccess: () => {
      toast.success('Leave approved');
      void qc.invalidateQueries({ queryKey: ['hr'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post(`/superadmin/hr/leaves/${id}/reject`, { rejection_reason: reason }),
    onSuccess: () => {
      toast.success('Leave rejected');
      setRejectingId(null);
      setRejectReason('');
      void qc.invalidateQueries({ queryKey: ['hr', 'leaves'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const pending = pendingQ.data?.items ?? [];
  const history = historyQ.data?.items ?? [];

  return (
    <>
      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-3 flex items-center justify-between">
            <div className="text-vt-14 font-semibold text-vt-text">Pending approvals</div>
            <Button size="sm" onClick={() => setApplyOpen(true)}>
              <Plus className="h-4 w-4" /> Apply leave
            </Button>
          </div>
          {pendingQ.isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : pending.length === 0 ? (
            <div className="rounded-vt-sm border border-dashed border-vt-border bg-vt-surface-2 p-vt-4 text-center text-vt-13 text-vt-text-soft">
              No pending leave requests.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{l.employee.full_name}</TableCell>
                    <TableCell>
                      <Badge variant="info">{LEAVE_TYPE_LABEL[l.leave_type]}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-vt-text-muted">
                      {formatDate(l.start_date)} – {formatDate(l.end_date)}
                    </TableCell>
                    <TableCell className="text-vt-text-muted">{l.days}</TableCell>
                    <TableCell className="max-w-[260px] truncate text-vt-text-muted">
                      {l.reason ?? '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-vt-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => approveMutation.mutate(l.id)}
                          loading={approveMutation.isPending && approveMutation.variables === l.id}
                        >
                          <Check className="h-4 w-4" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setRejectingId(l.id);
                            setRejectReason('');
                          }}
                        >
                          <XIcon className="h-4 w-4" /> Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-3 text-vt-14 font-semibold text-vt-text">Leave history</div>
          {historyQ.isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : history.length === 0 ? (
            <div className="rounded-vt-sm border border-dashed border-vt-border bg-vt-surface-2 p-vt-4 text-center text-vt-13 text-vt-text-soft">
              No leave records yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filed</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="whitespace-nowrap text-vt-text-muted">
                      {formatDateTime(l.created_at)}
                    </TableCell>
                    <TableCell>{l.employee.full_name}</TableCell>
                    <TableCell>{LEAVE_TYPE_LABEL[l.leave_type]}</TableCell>
                    <TableCell className="whitespace-nowrap text-vt-text-muted">
                      {formatDate(l.start_date)} – {formatDate(l.end_date)}
                    </TableCell>
                    <TableCell className="text-vt-text-muted">{l.days}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          l.status === 'approved'
                            ? 'ok'
                            : l.status === 'rejected'
                              ? 'alert'
                              : l.status === 'cancelled'
                                ? 'muted'
                                : 'warn'
                        }
                      >
                        {l.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Apply leave dialog */}
      <Dialog
        open={applyOpen}
        onOpenChange={(o) => {
          setApplyOpen(o);
          if (!o) form.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply leave</DialogTitle>
            <DialogDescription>
              Create a pending leave request. It will appear in the approval queue.
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-vt-3"
            onSubmit={form.handleSubmit((v) => applyMutation.mutate(v))}
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="lv-emp">Employee</Label>
              <Select id="lv-emp" {...form.register('employee_id')}>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.full_name} — {DEPT_LABEL[e.department]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="lv-type">Type</Label>
                <Select id="lv-type" {...form.register('leave_type')}>
                  {LEAVE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {LEAVE_TYPE_LABEL[t]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="lv-start">Start</Label>
                <Input id="lv-start" type="date" {...form.register('start_date')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="lv-end">End</Label>
                <Input id="lv-end" type="date" {...form.register('end_date')} />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="lv-reason">Reason (optional)</Label>
                <Textarea id="lv-reason" rows={3} {...form.register('reason')} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setApplyOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={applyMutation.isPending} loadingText="Applying…">
                Apply leave
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog
        open={rejectingId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setRejectingId(null);
            setRejectReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject leave</DialogTitle>
            <DialogDescription>Please share the reason — it's visible to the employee.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectingId(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (rejectingId && rejectReason.trim().length >= 2) {
                  rejectMutation.mutate({ id: rejectingId, reason: rejectReason.trim() });
                } else {
                  toast.error('Please enter a reason');
                }
              }}
              loading={rejectMutation.isPending}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Payroll tab
// ──────────────────────────────────────────────────────────────────────
function PayrollTab({ employees }: { employees: Employee[] }): JSX.Element {
  const qc = useQueryClient();
  const [period, setPeriod] = useState(thisMonth());
  const [payingId, setPayingId] = useState<string | null>(null);
  const [paidVia, setPaidVia] = useState<'bank_transfer' | 'upi' | 'cash' | 'cheque'>('bank_transfer');
  const [paymentRef, setPaymentRef] = useState('');

  const listQ = useQuery<{ items: PayrollRow[] }>({
    queryKey: ['hr', 'payroll', period],
    queryFn: () => get(`/superadmin/hr/payroll?period_label=${period}&limit=100`)
  });

  const runMutation = useMutation({
    mutationFn: () => post('/superadmin/hr/payroll/run-month', { period_label: period }),
    onSuccess: () => {
      toast.success(`Payroll generated for ${period}`);
      void qc.invalidateQueries({ queryKey: ['hr', 'payroll'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const markPaidMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      post(`/superadmin/hr/payroll/${id}/mark-paid`, {
        paid_via: paidVia,
        payment_reference: paymentRef || undefined
      }),
    onSuccess: () => {
      toast.success('Marked paid');
      void qc.invalidateQueries({ queryKey: ['hr', 'payroll'] });
      setPayingId(null);
      setPaymentRef('');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const downloadCsv = (): void => {
    void api
      .get('/superadmin/hr/payroll/export.csv', {
        responseType: 'blob',
        params: { period_label: period }
      })
      .then((res) => {
        const blob = new Blob([res.data as Blob], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hr-payroll-${period}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="mb-vt-3 flex flex-wrap items-center justify-between gap-vt-2">
          <div className="flex items-center gap-vt-2">
            <Label htmlFor="pr-month" className="text-vt-13 text-vt-text-muted">
              Period:
            </Label>
            <Input
              id="pr-month"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex items-center gap-vt-2">
            <Button size="sm" onClick={() => runMutation.mutate()} loading={runMutation.isPending}>
              <Receipt className="h-4 w-4" /> Run payroll
            </Button>
            <Button size="sm" variant="secondary" onClick={downloadCsv} disabled={items.length === 0}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>

        {listQ.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Receipt className="h-6 w-6" strokeWidth={1.75} />}
            title="No payroll for this period yet"
            description={`Click "Run payroll" to generate ${period} payroll for ${employees.filter((e) => e.status === 'active').length} active employees.`}
            action={
              <Button onClick={() => runMutation.mutate()} loading={runMutation.isPending}>
                <Receipt className="h-4 w-4" /> Run payroll
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Base CTC</TableHead>
                <TableHead className="text-right">Days worked</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">Bonus</TableHead>
                <TableHead className="text-right">TDS</TableHead>
                <TableHead className="text-right">Net pay</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Paid at</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="font-medium text-vt-text">{p.employee.full_name}</div>
                    <div className="text-vt-12 text-vt-text-soft">{DEPT_LABEL[p.employee.department]}</div>
                  </TableCell>
                  <TableCell className="text-right text-vt-text-muted">{formatInr(p.base_ctc_inr)}</TableCell>
                  <TableCell className="text-right text-vt-text-muted">
                    {p.attendance_days_worked} / {p.attendance_total_days}
                  </TableCell>
                  <TableCell className="text-right text-vt-text-muted">{formatInr(p.deductions_inr)}</TableCell>
                  <TableCell className="text-right text-vt-text-muted">{formatInr(p.bonus_inr)}</TableCell>
                  <TableCell className="text-right text-vt-text-muted">{formatInr(p.tds_inr)}</TableCell>
                  <TableCell className="text-right font-semibold text-vt-text">{formatInr(p.net_pay_inr)}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        p.status === 'paid'
                          ? 'ok'
                          : p.status === 'cancelled'
                            ? 'muted'
                            : p.status === 'pending'
                              ? 'warn'
                              : 'default'
                      }
                    >
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-vt-text-muted">
                    {p.paid_at ? formatDateTime(p.paid_at) : '—'}
                  </TableCell>
                  <TableCell>
                    {p.status !== 'paid' && p.status !== 'cancelled' ? (
                      <Button size="sm" variant="ghost" onClick={() => setPayingId(p.id)}>
                        Mark paid
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={payingId !== null}
          onOpenChange={(o) => {
            if (!o) {
              setPayingId(null);
              setPaymentRef('');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark payroll paid</DialogTitle>
              <DialogDescription>Capture the payment channel + reference for audit.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="pr-via">Paid via</Label>
                <Select id="pr-via" value={paidVia} onChange={(e) => setPaidVia(e.target.value as 'bank_transfer' | 'upi' | 'cash' | 'cheque')}>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="pr-ref">Payment reference (optional)</Label>
                <Input id="pr-ref" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPayingId(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => payingId && markPaidMutation.mutate({ id: payingId })}
                loading={markPaidMutation.isPending}
              >
                Confirm paid
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Hiring tab
// ──────────────────────────────────────────────────────────────────────
const JobOpeningFormSchema = z.object({
  title: z.string().trim().min(2, 'Required').max(160),
  department: z.enum(DEPARTMENTS),
  employment_type: z.enum(EMPLOYMENT_TYPES),
  location: z.string().trim().max(80).optional(),
  description: z.string().trim().max(4000).optional(),
  min_ctc_inr: z.coerce.number().int().min(0).max(100_000_000).optional(),
  max_ctc_inr: z.coerce.number().int().min(0).max(100_000_000).optional()
});
type JobOpeningForm = z.infer<typeof JobOpeningFormSchema>;

const ApplicantFormSchema = z.object({
  job_opening_id: z.string().uuid(),
  full_name: z.string().trim().min(2, 'Required').max(120),
  phone: z.string().trim().regex(/^\+?[1-9]\d{6,14}$/, 'Invalid phone'),
  email: z.string().trim().email().max(160),
  expected_ctc_inr: z.coerce.number().int().min(0).max(100_000_000).optional(),
  experience_years: z.coerce.number().min(0).max(60).optional()
});
type ApplicantForm = z.infer<typeof ApplicantFormSchema>;

function HiringTab({ employees: _employees }: { employees: Employee[] }): JSX.Element {
  const qc = useQueryClient();
  const [openingOpen, setOpeningOpen] = useState(false);
  const [applicantOpen, setApplicantOpen] = useState(false);
  const [selectedOpeningId, setSelectedOpeningId] = useState<string | null>(null);

  const openingsQ = useQuery<{ items: JobOpening[] }>({
    queryKey: ['hr', 'job-openings'],
    queryFn: () => get('/superadmin/hr/job-openings?limit=50')
  });

  const openings = openingsQ.data?.items ?? [];
  const focusOpeningId = selectedOpeningId ?? openings[0]?.id ?? null;

  const applicantsQ = useQuery<{ items: Applicant[] }>({
    queryKey: ['hr', 'applicants', focusOpeningId],
    enabled: focusOpeningId !== null,
    queryFn: () => get(`/superadmin/hr/job-openings/${focusOpeningId}/applicants`)
  });

  const openingForm = useForm<JobOpeningForm>({
    resolver: zodResolver(JobOpeningFormSchema),
    defaultValues: {
      title: '',
      department: 'engineering',
      employment_type: 'full_time'
    }
  });
  const applicantForm = useForm<ApplicantForm>({
    resolver: zodResolver(ApplicantFormSchema),
    defaultValues: { job_opening_id: focusOpeningId ?? '', full_name: '', phone: '', email: '' }
  });

  const createOpening = useMutation({
    mutationFn: (v: JobOpeningForm) => post('/superadmin/hr/job-openings', v),
    onSuccess: () => {
      toast.success('Position opened');
      void qc.invalidateQueries({ queryKey: ['hr', 'job-openings'] });
      setOpeningOpen(false);
      openingForm.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const toggleOpen = useMutation({
    mutationFn: ({ id, is_open }: { id: string; is_open: boolean }) =>
      patch(`/superadmin/hr/job-openings/${id}`, { is_open }),
    onSuccess: () => {
      toast.success('Position updated');
      void qc.invalidateQueries({ queryKey: ['hr', 'job-openings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const createApplicant = useMutation({
    mutationFn: (v: ApplicantForm) => post('/superadmin/hr/applicants', v),
    onSuccess: () => {
      toast.success('Applicant added');
      void qc.invalidateQueries({ queryKey: ['hr', 'applicants'] });
      setApplicantOpen(false);
      applicantForm.reset({ job_opening_id: focusOpeningId ?? '', full_name: '', phone: '', email: '' });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const moveStage = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: HiringStage }) =>
      post(`/superadmin/hr/applicants/${id}/move-to/${stage}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hr', 'applicants'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const stageGroups: Record<HiringStage, Applicant[]> = {
    open: [],
    screening: [],
    interview_round_1: [],
    interview_round_2: [],
    final_round: [],
    offer_extended: [],
    hired: [],
    rejected: [],
    withdrawn: []
  };
  for (const a of applicantsQ.data?.items ?? []) {
    stageGroups[a.stage].push(a);
  }

  return (
    <>
      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-3 flex items-center justify-between">
            <div className="text-vt-14 font-semibold text-vt-text">Open positions</div>
            <Button size="sm" onClick={() => setOpeningOpen(true)}>
              <Plus className="h-4 w-4" /> New position
            </Button>
          </div>
          {openingsQ.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : openings.length === 0 ? (
            <EmptyState
              icon={<Building2 className="h-6 w-6" strokeWidth={1.75} />}
              title="No positions yet"
              description="Create your first opening and start tracking applicants."
            />
          ) : (
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2 xl:grid-cols-3">
              {openings.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setSelectedOpeningId(o.id)}
                  className={
                    'rounded-vt-md border p-vt-4 text-left transition ' +
                    (focusOpeningId === o.id
                      ? 'border-vt-primary bg-vt-primary-100/40 shadow-vt-1'
                      : 'border-vt-border bg-vt-surface hover:bg-vt-surface-2')
                  }
                >
                  <div className="flex items-start justify-between gap-vt-2">
                    <div>
                      <div className="font-semibold text-vt-text">{o.title}</div>
                      <div className="mt-1 text-vt-12 text-vt-text-soft">
                        {DEPT_LABEL[o.department]} · {EMPLOYMENT_LABEL[o.employment_type]}
                        {o.location ? ` · ${o.location}` : ''}
                      </div>
                    </div>
                    <Badge variant={o.is_open ? 'ok' : 'muted'}>{o.is_open ? 'Open' : 'Closed'}</Badge>
                  </div>
                  <div className="mt-vt-3 flex items-center justify-between text-vt-12">
                    <span className="text-vt-text-muted">
                      {o.min_ctc_inr || o.max_ctc_inr
                        ? `${formatInr(o.min_ctc_inr)} – ${formatInr(o.max_ctc_inr)}`
                        : 'CTC not disclosed'}
                    </span>
                    <span className="text-vt-text-soft">{o._count?.applicants ?? 0} applicants</span>
                  </div>
                  <div className="mt-vt-2 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        toggleOpen.mutate({ id: o.id, is_open: !o.is_open });
                      }}
                      className="text-vt-12 text-vt-primary hover:underline"
                    >
                      {o.is_open ? 'Close' : 'Re-open'}
                    </button>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-3 flex items-center justify-between">
            <div className="text-vt-14 font-semibold text-vt-text">
              Applicants {focusOpeningId ? `— ${openings.find((o) => o.id === focusOpeningId)?.title ?? ''}` : ''}
            </div>
            <Button
              size="sm"
              disabled={!focusOpeningId}
              onClick={() => {
                applicantForm.reset({
                  job_opening_id: focusOpeningId ?? '',
                  full_name: '',
                  phone: '',
                  email: ''
                });
                setApplicantOpen(true);
              }}
            >
              <UserPlus className="h-4 w-4" /> Add applicant
            </Button>
          </div>
          {!focusOpeningId ? (
            <div className="rounded-vt-sm border border-dashed border-vt-border bg-vt-surface-2 p-vt-4 text-center text-vt-13 text-vt-text-soft">
              Open a position above to see its pipeline.
            </div>
          ) : applicantsQ.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {HIRING_STAGES.map((stage) => {
                const list = stageGroups[stage];
                if (list.length === 0 && (stage === 'rejected' || stage === 'withdrawn')) return null;
                return (
                  <div
                    key={stage}
                    className="flex min-h-[120px] flex-col gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3"
                  >
                    <div className="mb-vt-1 flex items-center justify-between">
                      <span className="text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                        {STAGE_LABEL[stage]}
                      </span>
                      <span className="text-vt-12 text-vt-text-soft">{list.length}</span>
                    </div>
                    {list.length === 0 ? (
                      <div className="text-vt-12 text-vt-text-soft">—</div>
                    ) : (
                      list.map((a) => (
                        <div
                          key={a.id}
                          className="rounded-vt-sm border border-vt-border bg-vt-surface p-vt-2 shadow-vt-1"
                        >
                          <div className="text-vt-13 font-medium text-vt-text">{a.full_name}</div>
                          <div className="text-vt-12 text-vt-text-soft">{a.email}</div>
                          <div className="mt-1 flex items-center justify-between text-vt-12 text-vt-text-muted">
                            <span>
                              {a.experience_years != null ? `${a.experience_years}y exp` : '—'}
                            </span>
                            <span>{a.expected_ctc_inr ? formatInr(a.expected_ctc_inr) : ''}</span>
                          </div>
                          <div className="mt-vt-2">
                            <Select
                              value={a.stage}
                              onChange={(e) =>
                                moveStage.mutate({ id: a.id, stage: e.target.value as HiringStage })
                              }
                            >
                              {HIRING_STAGES.map((s) => (
                                <option key={s} value={s}>
                                  {STAGE_LABEL[s]}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New position dialog */}
      <Dialog
        open={openingOpen}
        onOpenChange={(o) => {
          setOpeningOpen(o);
          if (!o) openingForm.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New position</DialogTitle>
            <DialogDescription>Define a hiring opening to start collecting applicants.</DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-vt-3"
            onSubmit={openingForm.handleSubmit((v) => createOpening.mutate(v))}
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="op-title">Title</Label>
              <Input id="op-title" autoFocus {...openingForm.register('title')} />
              {openingForm.formState.errors.title && (
                <p className="text-vt-12 text-vt-alert">{openingForm.formState.errors.title.message}</p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="op-dept">Department</Label>
                <Select id="op-dept" {...openingForm.register('department')}>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {DEPT_LABEL[d]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="op-type">Employment type</Label>
                <Select id="op-type" {...openingForm.register('employment_type')}>
                  {EMPLOYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {EMPLOYMENT_LABEL[t]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="op-loc">Location</Label>
                <Input id="op-loc" {...openingForm.register('location')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="op-min">Min CTC (yearly)</Label>
                <Input id="op-min" type="number" min="0" {...openingForm.register('min_ctc_inr')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="op-max">Max CTC (yearly)</Label>
                <Input id="op-max" type="number" min="0" {...openingForm.register('max_ctc_inr')} />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="op-desc">Description</Label>
                <Textarea id="op-desc" rows={4} {...openingForm.register('description')} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpeningOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={createOpening.isPending} loadingText="Creating…">
                Open position
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add applicant dialog */}
      <Dialog
        open={applicantOpen}
        onOpenChange={(o) => {
          setApplicantOpen(o);
          if (!o) applicantForm.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add applicant</DialogTitle>
            <DialogDescription>Capture inbound interest into the hiring pipeline.</DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-vt-3"
            onSubmit={applicantForm.handleSubmit((v) => createApplicant.mutate(v))}
          >
            <input type="hidden" {...applicantForm.register('job_opening_id')} />
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ap-name">Full name</Label>
                <Input id="ap-name" autoFocus {...applicantForm.register('full_name')} />
                {applicantForm.formState.errors.full_name && (
                  <p className="text-vt-12 text-vt-alert">{applicantForm.formState.errors.full_name.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ap-phone">Phone</Label>
                <Input id="ap-phone" placeholder="+919999999999" {...applicantForm.register('phone')} />
                {applicantForm.formState.errors.phone && (
                  <p className="text-vt-12 text-vt-alert">{applicantForm.formState.errors.phone.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="ap-email">Email</Label>
                <Input id="ap-email" type="email" {...applicantForm.register('email')} />
                {applicantForm.formState.errors.email && (
                  <p className="text-vt-12 text-vt-alert">{applicantForm.formState.errors.email.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ap-exp">Experience (years)</Label>
                <Input id="ap-exp" type="number" min="0" step="0.5" {...applicantForm.register('experience_years')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ap-ctc">Expected CTC (yearly)</Label>
                <Input id="ap-ctc" type="number" min="0" {...applicantForm.register('expected_ctc_inr')} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setApplicantOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={createApplicant.isPending} loadingText="Adding…">
                Add applicant
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
